import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { login, fetchMe } from '@/api/auth';
import { useAuth } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const schema = z.object({
  email: z.string().email('Valid email required'),
  password: z.string().min(1, 'Required'),
});
type Values = z.infer<typeof schema>;

export function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  async function onSubmit(values: Values) {
    setError(null);
    try {
      const res = await login(values.email, values.password);
      if (res.mfa_required && res.challenge_token) {
        navigate('/mfa/verify', { state: { challengeToken: res.challenge_token } });
        return;
      }
      if (res.access_token) {
        useAuth.setState({ accessToken: res.access_token });
        const me = await fetchMe();
        useAuth.getState().setSession({
          accessToken: res.access_token,
          user: me.user,
          tenant: me.tenant,
        });
        if (me.user.must_change_password) navigate('/set-password');
        else navigate('/dashboard');
      } else {
        setError('Unexpected response.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    }
  }

  return (
    <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-6">
        Portal access
      </p>
      <Card>
        <CardHeader>
          <CardTitle>Log in</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" noValidate onSubmit={handleSubmit(onSubmit)}>
            {/* Email */}
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={errors.email != null}
                aria-describedby={errors.email ? 'email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p id="email-error" role="alert" className="text-xs text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password != null}
                aria-describedby={errors.password ? 'password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p id="password-error" role="alert" className="text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Server / network error */}
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <div className="flex justify-between text-xs text-muted-foreground">
              <Link to="/forgot-password" className="hover:underline">
                Forgot password?
              </Link>
              <Link to="/contact" className="hover:underline">
                Need access?
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
